import React, { useState } from 'react';
import styled from 'styled-components';
import { useToast } from './ToastContainer';
import Loader from './Loader';

import ImportIcon from '../assets/my_store.svg';
import Logo from '../assets/logo.svg';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background-color: #172A2A;
  border-radius: 24px;
  padding: 40px;
  width: 90%;
  max-width: 600px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const HeaderSection = styled.div`
  background-color: #1F3737;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  align-self: center;
  width: fit-content;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-family: Ubuntu, sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
`;

const FieldInput = styled.input`
  background: transparent;
  border: none;
  border-bottom: 1px solid #284640;
  padding: 12px 0;
  font-family: Ubuntu, sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #FFFFFF;
  
  &::placeholder {
    color: #4A5F5F;
  }
  
  &:focus {
    outline: none;
    border-bottom-color: #609F94;
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const ImportButton = styled.button`
  background-color: #24BA7D;
  border: none;
  border-radius: 9999px;
  padding: 12px 0;
  width: 100%;
  font-family: Ubuntu, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5;
  color: #FFFFFF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  
  &:hover {
    background-color: #20A568;
  }
  
  &:disabled {
    background-color: #4F6F66;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 2px;
    background-color: #8B9494;
  }
  
  &::before {
    transform: rotate(45deg);
  }
  
  &::after {
    transform: rotate(-45deg);
  }
  
  &:hover {
    &::before,
    &::after {
      background-color: #A5B1B1;
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(23, 42, 42, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  z-index: 100;
  backdrop-filter: blur(2px);
`;



interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (credentials: { login: string; password: string }) => Promise<void>;
}

const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, onImport }) => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!login.trim() || !password.trim()) {
      showError('Заполните все поля');
      return;
    }

    setIsLoading(true);

    try {
      await onImport({ login: login.trim(), password: password.trim() });

      // Сброс формы
      setLogin('');
      setPassword('');
      onClose();
    } catch (err: any) {
      // onImport уже обработает ошибку через useApiCall, но если что-то пойдет не так локально
      showError(`Ошибка при импорте: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} />

        <HeaderSection>
          <IconWrapper>
            <img src={Logo} alt="Logo" />
          </IconWrapper>
          <IconWrapper>
            <img src={ImportIcon} alt="Import" />
          </IconWrapper>
        </HeaderSection>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <FormField>
              <FieldLabel>Логин Мой склад</FieldLabel>
              <FieldInput
                type="text"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="Введите ваш логин"
                disabled={isLoading}
              />
            </FormField>

            <FormField>
              <FieldLabel>Пароль Мой склад</FieldLabel>
              <FieldInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Введите ваш пароль"
                disabled={isLoading}
              />
            </FormField>
          </FormSection>

          <ImportButton
            type="submit"
            disabled={isLoading || !login.trim() || !password.trim()}
          >
            {isLoading ? 'Импорт...' : 'Импортировать'}
          </ImportButton>
        </form>

        {isLoading && (
          <LoadingOverlay>
            <Loader text="Импорт товаров..." />
          </LoadingOverlay>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ImportModal; 